<script setup>
// "Conversations by agent" and "by team" of the live overview (R1), on the kit's table: name (with the agent's avatar
// and availability), open and unattended, most open first. Paginated with the kit's footer, and the page size is
// remembered per user in `ui_settings` as before (the woot's `report_overview_*_table_page_size`). No height cap: the
// page scrolls once (the table is as tall as one page of rows).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty,
} from 'dashboard/components-next/polaris/ui/table';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import UserAvatar from 'dashboard/components-next/polaris/cards/UserAvatar.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';

const props = defineProps({
  rows: { type: Array, default: () => [] }, // [{ id, name, email?, thumbnail?, status?, open, unattended }]
  nameHeader: { type: String, required: true },
  pageSizeKey: { type: String, required: true },
  countKey: { type: String, required: true },
  showingKey: { type: String, required: true },
  emptyText: { type: String, required: true },
  isLoading: Boolean,
  people: Boolean, // agents: avatar with availability and e-mail
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const PAGE_SIZES = [10, 20, 50, 100];
const pageSizeOptions = PAGE_SIZES.map(size => ({
  value: size,
  label: t('REPORT.PAGINATION.PER_PAGE_TEMPLATE', { size }),
}));
const pageSize = computed(() => uiSettings.value[props.pageSizeKey] || 10);
const setPageSize = size => {
  updateUISettings({ [props.pageSizeKey]: Number(size) });
};

const page = ref(1);
const pages = computed(() =>
  Math.max(1, Math.ceil(props.rows.length / pageSize.value))
);
watch([pageSize, pages], () => {
  if (page.value > pages.value) page.value = pages.value;
});
const visible = computed(() =>
  props.rows.slice(
    (page.value - 1) * pageSize.value,
    page.value * pageSize.value
  )
);
const firstLoad = computed(() => props.isLoading && !props.rows.length);
const number = value => Number(value).toLocaleString();
</script>

<template>
  <div class="grid min-w-0 gap-3" data-live-table>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{{ nameHeader }}</TableHead>
          <TableHead class="w-32 text-end">
            {{ t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN') }}
          </TableHead>
          <TableHead class="w-32 text-end">
            {{
              t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED')
            }}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="firstLoad">
          <TableRow v-for="row in 3" :key="row">
            <TableCell><Skeleton class="h-5 w-48" /></TableCell>
            <TableCell><Skeleton class="ms-auto h-5 w-8" /></TableCell>
            <TableCell><Skeleton class="ms-auto h-5 w-8" /></TableCell>
          </TableRow>
        </template>
        <TableEmpty v-else-if="!rows.length" :colspan="3">
          <span class="text-polaris-muted-foreground">{{ emptyText }}</span>
        </TableEmpty>
        <template v-else>
          <TableRow v-for="row in visible" :key="row.id">
            <TableCell>
              <div class="flex min-w-36 items-center gap-2">
                <UserAvatar
                  v-if="people"
                  :name="row.name"
                  :src="row.thumbnail || ''"
                  :status="row.status || ''"
                  :size="28"
                  decorative
                />
                <div class="grid min-w-0">
                  <span class="truncate font-medium">{{ row.name }}</span>
                  <span
                    v-if="row.email"
                    class="truncate type-polaris-caption text-polaris-muted-foreground"
                  >
                    {{ row.email }}
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell
              class="text-end tabular-nums"
              :class="row.open ? '' : 'text-polaris-muted-foreground'"
            >
              {{ number(row.open) }}
            </TableCell>
            <TableCell
              class="text-end tabular-nums"
              :class="row.unattended ? '' : 'text-polaris-muted-foreground'"
            >
              {{ number(row.unattended) }}
            </TableCell>
          </TableRow>
        </template>
      </TableBody>
    </Table>
    <div
      v-if="rows.length"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <div class="w-36">
        <OptionSelect
          :model-value="pageSize"
          :options="pageSizeOptions"
          size="sm"
          :aria-label="t('POLARIS_REPORTS.LIVE_TABLE.PAGE_SIZE')"
          @update:model-value="setPageSize"
        />
      </div>
      <PaginationFooter
        v-model:page="page"
        class="flex-1"
        :total="rows.length"
        :page-size="pageSize"
        :count-key="countKey"
        :showing-key="showingKey"
      />
    </div>
  </div>
</template>
